﻿import dayjs from '@/helpers/dayjs'
import {
  ICustomField,
  ICustomFieldOption,
  ICustomFieldValue,
} from '@/api/types/response.types'
import ColorAssistant from '@/helpers/color-assistant'

/* Mirrors Domain.Entities.Types.CustomFieldTypes */
export enum ECustomFieldTypes {
  Text = 0,
  Number = 1,
  Dropdown = 2,
  Date = 3,
  Checkbox = 4,
}

/* Mirrors Domain.Entities.Types.CustomFieldPermissions */
export enum ECustomFieldPermissions {
  None = 0,
  View = 1,
  ViewAndEdit = 2,
}

/* Mirrors Domain.Entities.Types.CustomFieldNumberTypes */
export enum ECustomFieldNumberTypes {
  Number = 0,
  Percentage = 1,
  Currency = 2,
  Unformatted = 3,
}

/* Mirrors Domain.Entities.Types.CustomFieldNumberLabelPositions */
export enum ECustomFieldNumberLabelPositions {
  Before = 0,
  After = 1,
}

/* Mirrors Domain.Entities.Types.CustomFieldTextTypes */
export enum ECustomFieldTextTypes {
  Unformatted = 0,
  Email = 1,
  PhoneNumber = 2,
  Url = 3,
}

export interface ICustomFieldSelectOption {
  id: number
  name: string
}

/* How a date field is filtered on the listing screens */
export enum ECustomFieldDateFilterModes {
  Any = 'any',
  None = 'none',
  Range = 'range',
}

/* How a number field is filtered on the listing screens */
export enum ECustomFieldNumberFilterModes {
  Any = 'any',
  None = 'none',
  Range = 'range',
}

/* Separates option IDs inside an encoded dropdown filter (a comma would be split into an array by the router) */
const OPTION_IDS_DELIMITER = '!'

/* Dropdown filters use option ID 0 to mean "no value", like appointee ID 0 means unappointed */
export const NO_OPTION_FILTER_ID = 0
export const NO_OPTION_FILTER_LABEL = 'None'

/* A listing filter on one custom field. Only the properties for the field's type are set. */
export interface ICustomFieldFilter {
  fieldID: number
  /* Dropdown: match items whose value is any of these options (or has none, when NO_OPTION_FILTER_ID is included) */
  optionIDs?: number[]
  /* Checkbox: true matches checked, false matches unchecked or never set */
  checked?: boolean
  /* Date */
  dateMode?: ECustomFieldDateFilterModes
  startDate?: string
  endDate?: string
  /* Number */
  numberMode?: ECustomFieldNumberFilterModes
  minValue?: number
  maxValue?: number
}

/* DECIMAL(25, 10) on the server leaves 15 digits before the decimal point */
const NUMBER_MAGNITUDE_LIMIT = 1e15

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_CHARACTERS_PATTERN = /^[\d\s()+.-]+$/

export default new class CustomFieldAssistant {
  public get FieldTypeOptions(): ICustomFieldSelectOption[] {
    return [
      { id: ECustomFieldTypes.Text, name: 'Text' },
      { id: ECustomFieldTypes.Number, name: 'Number' },
      { id: ECustomFieldTypes.Dropdown, name: 'Dropdown' },
      { id: ECustomFieldTypes.Date, name: 'Date' },
      { id: ECustomFieldTypes.Checkbox, name: 'Checkbox' },
    ]
  }

  public get PermissionOptions(): ICustomFieldSelectOption[] {
    return [
      { id: ECustomFieldPermissions.None, name: 'Not see this field' },
      { id: ECustomFieldPermissions.View, name: 'View only' },
      { id: ECustomFieldPermissions.ViewAndEdit, name: 'View and edit' },
    ]
  }

  public get NumberTypeOptions(): ICustomFieldSelectOption[] {
    return [
      { id: ECustomFieldNumberTypes.Number, name: 'Number' },
      { id: ECustomFieldNumberTypes.Percentage, name: 'Percentage' },
      { id: ECustomFieldNumberTypes.Currency, name: 'Currency' },
      { id: ECustomFieldNumberTypes.Unformatted, name: 'Unformatted' },
    ]
  }

  public get NumberLabelPositionOptions(): ICustomFieldSelectOption[] {
    return [
      {
        id: ECustomFieldNumberLabelPositions.Before,
        name: 'Before the number',
      },
      { id: ECustomFieldNumberLabelPositions.After, name: 'After the number' },
    ]
  }

  public get TextTypeOptions(): ICustomFieldSelectOption[] {
    return [
      { id: ECustomFieldTextTypes.Unformatted, name: 'Any text' },
      { id: ECustomFieldTextTypes.Email, name: 'Email address' },
      { id: ECustomFieldTextTypes.PhoneNumber, name: 'Phone number' },
      { id: ECustomFieldTextTypes.Url, name: 'URL' },
    ]
  }

  public getFieldTypeName(type: number): string {
    const option = this.FieldTypeOptions.find(o => o.id === type)
    return option ? option.name : ''
  }

  /* Name of the ddi icon used to represent a field type */
  public getFieldTypeIcon(type: number): string {
    switch (type) {
      case ECustomFieldTypes.Text:
        return 'text-style'
      case ECustomFieldTypes.Number:
        return 'numbered-list'
      case ECustomFieldTypes.Dropdown:
        return 'caret-down'
      case ECustomFieldTypes.Date:
        return 'calendar'
      case ECustomFieldTypes.Checkbox:
        return 'check'
      default:
        return 'list'
    }
  }

  public getPermissionShortName(permission: number): string {
    switch (permission) {
      case ECustomFieldPermissions.View:
        return 'view'
      case ECustomFieldPermissions.ViewAndEdit:
        return 'view & edit'
      default:
        return 'hidden'
    }
  }

  /* ---------- Value display ---------- */

  /* True when the value carries data for the field's type */
  public hasValue(
    field: ICustomField,
    value: ICustomFieldValue | null,
  ): boolean {
    if (!value) {
      return false
    }
    switch (field.customFieldType) {
      case ECustomFieldTypes.Text:
        return !!value.textData
      case ECustomFieldTypes.Number:
        return value.numberData !== null && value.numberData !== undefined
      case ECustomFieldTypes.Dropdown:
        return !!this.optionFor(field, value)
      case ECustomFieldTypes.Date:
        return !!value.dateData
      case ECustomFieldTypes.Checkbox:
        return value.checkboxData !== null && value.checkboxData !== undefined
      default:
        return false
    }
  }

  public optionFor(
    field: ICustomField,
    value: ICustomFieldValue | null,
  ): ICustomFieldOption | null {
    if (!value || !value.optionID) {
      return null
    }
    return (field.options || []).find(o => o.id === value.optionID) || null
  }

  /*
   * Percentages always get a % sign. Currency defaults to $ and everything else is bare,
   * unless the field has its own label.
   */
  public formatNumber(field: ICustomField, number: number): string {
    const decimals = Math.max(0, field.numberDecimalPlaces || 0)
    const isNegative = number < 0
    const magnitude = Math.abs(number)
    const sign = isNegative ? '-' : ''

    const digits =
      field.numberType === ECustomFieldNumberTypes.Unformatted
        ? magnitude.toFixed(decimals)
        : magnitude.toLocaleString(undefined, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          })

    if (field.numberType === ECustomFieldNumberTypes.Percentage) {
      return `${sign}${digits}%`
    }

    let label = (field.numberLabel || '').trim()
    let position = field.numberLabelPosition

    if (!label && field.numberType === ECustomFieldNumberTypes.Currency) {
      label = '$'
      position = ECustomFieldNumberLabelPositions.Before
    }

    if (!label) {
      return `${sign}${digits}`
    }

    if (position === ECustomFieldNumberLabelPositions.Before) {
      // Symbols hug the number ($12.00); words get a space (approx 12)
      const separator = label.length > 1 ? ' ' : ''
      return `${sign}${label}${separator}${digits}`
    }

    return `${sign}${digits} ${label}`
  }

  public formatDate(date: string): string {
    return dayjs(date).format('MMM D, YYYY')
  }

  /* Plain-text rendering of a value, used wherever a value is shown read-only */
  public displayText(
    field: ICustomField,
    value: ICustomFieldValue | null,
  ): string {
    if (!this.hasValue(field, value)) {
      return ''
    }
    switch (field.customFieldType) {
      case ECustomFieldTypes.Text:
        return value.textData
      case ECustomFieldTypes.Number:
        return this.formatNumber(field, value.numberData)
      case ECustomFieldTypes.Date:
        return this.formatDate(value.dateData)
      case ECustomFieldTypes.Checkbox:
        return value.checkboxData ? 'Yes' : 'No'
      case ECustomFieldTypes.Dropdown: {
        const option = this.optionFor(field, value)
        return option ? option.name : ''
      }
      default:
        return ''
    }
  }

  /* ---------- Value input ---------- */

  public placeholderFor(field: ICustomField): string {
    if (field.customFieldType === ECustomFieldTypes.Text) {
      switch (field.textType) {
        case ECustomFieldTextTypes.Email:
          return 'name@example.com'
        case ECustomFieldTextTypes.PhoneNumber:
          return '(555) 555-5555'
        case ECustomFieldTextTypes.Url:
          return 'https://'
        default:
          return 'Add a value'
      }
    }
    if (field.customFieldType === ECustomFieldTypes.Number) {
      if (field.numberType === ECustomFieldNumberTypes.Percentage) {
        return 'e.g. 25'
      }
      return (0).toFixed(field.numberDecimalPlaces || 0)
    }
    return ''
  }

  /* Step for a numeric input, matching the field's decimal places */
  public numberStepFor(field: ICustomField): string {
    const decimals = field.numberDecimalPlaces || 0
    return decimals > 0 ? `0.${'0'.repeat(decimals - 1)}1` : '1'
  }

  /* Only free-form text can span lines; email, phone and URL values are single-line by nature */
  public isMultilineText(field: ICustomField): boolean {
    return (
      field.customFieldType === ECustomFieldTypes.Text &&
      field.textType === ECustomFieldTextTypes.Unformatted
    )
  }

  /* ---------- Value validation (mirrors the server-side rules) ---------- */

  /* Returns an error message for the text, or null when it satisfies the field's settings */
  public validateText(field: ICustomField, rawText: string): string | null {
    const text = String(rawText || '').trim()
    if (text.length === 0) {
      return `${field.name} needs some text.`
    }
    if (text.length > 500) {
      return `${field.name} cannot be longer than 500 characters.`
    }
    if (
      field.textMinLength !== null &&
      field.textMinLength !== undefined &&
      text.length < field.textMinLength
    ) {
      return `${field.name} must be at least ${field.textMinLength} characters.`
    }
    if (
      field.textMaxLength !== null &&
      field.textMaxLength !== undefined &&
      text.length > field.textMaxLength
    ) {
      return `${field.name} cannot be longer than ${
        field.textMaxLength
      } characters.`
    }
    switch (field.textType) {
      case ECustomFieldTextTypes.Email:
        if (!EMAIL_PATTERN.test(text)) {
          return `${field.name} must be a valid email address.`
        }
        break
      case ECustomFieldTextTypes.PhoneNumber:
        if (
          !PHONE_CHARACTERS_PATTERN.test(text) ||
          text.replace(/\D/g, '').length < 7
        ) {
          return `${field.name} must be a valid phone number.`
        }
        break
      case ECustomFieldTextTypes.Url:
        if (!this.isValidHttpUrl(text)) {
          return `${
            field.name
          } must be a valid URL starting with http:// or https://.`
        }
        break
    }
    return null
  }

  /* Returns an error message for the typed number, or null when it satisfies the field's settings */
  public validateNumber(field: ICustomField, rawNumber: string): string | null {
    const raw = String(rawNumber === null ? '' : rawNumber).trim()
    if (raw.length === 0) {
      return `${field.name} needs a number.`
    }
    const number = Number(raw)
    if (!isFinite(number)) {
      return `${field.name} must be a number.`
    }
    if (Math.abs(number) >= NUMBER_MAGNITUDE_LIMIT) {
      return `${field.name} is too large.`
    }
    const decimals = field.numberDecimalPlaces || 0
    const decimalPart = raw.includes('.') ? raw.split('.')[1] : ''
    if (decimalPart.replace(/0+$/, '').length > decimals) {
      return decimals === 0
        ? `${field.name} must be a whole number.`
        : `${field.name} can have at most ${decimals} decimal place${
            decimals === 1 ? '' : 's'
          }.`
    }
    if (
      field.numberMinValue !== null &&
      field.numberMinValue !== undefined &&
      number < field.numberMinValue
    ) {
      return `${field.name} must be at least ${field.numberMinValue}.`
    }
    if (
      field.numberMaxValue !== null &&
      field.numberMaxValue !== undefined &&
      number > field.numberMaxValue
    ) {
      return `${field.name} cannot be more than ${field.numberMaxValue}.`
    }
    return null
  }

  public isValidHttpUrl(text: string): boolean {
    try {
      const url = new URL(text)
      return url.protocol === 'http:' || url.protocol === 'https:'
    } catch {
      return false
    }
  }

  /* ---------- Listing filters ---------- */

  /* Text fields are searched through the title/description box instead */
  public isFilterableField(field: ICustomField): boolean {
    return field.customFieldType !== ECustomFieldTypes.Text
  }

  /*
   * Filters travel in the query string and to the API as one compact string so the existing
   * trait-set plumbing can treat them as a single value: "fieldID:kind:value" entries joined
   * by "|", e.g. "12:o:3!4|15:c:1|18:d:2024-01-01_2024-01-31|21:n:10_99.5". Option IDs use "!" rather
   * than "," because the router parses comma lists in the query string into arrays. The server parses the same
   * format in Domain.Entities.App.InputParams.CustomFieldFilter.
   */
  public serializeFilters(filters: ICustomFieldFilter[]): string {
    return (filters || [])
      .map(f => this.serializeFilter(f))
      .filter(entry => entry.length > 0)
      .join('|')
  }

  public serializeFilter(filter: ICustomFieldFilter): string {
    if (!filter) {
      return ''
    }
    if (filter.optionIDs && filter.optionIDs.length > 0) {
      return `${filter.fieldID}:o:${filter.optionIDs.join(
        OPTION_IDS_DELIMITER,
      )}`
    }
    if (filter.checked !== undefined && filter.checked !== null) {
      return `${filter.fieldID}:c:${filter.checked ? 1 : 0}`
    }
    switch (filter.dateMode) {
      case ECustomFieldDateFilterModes.Any:
        return `${filter.fieldID}:d:any`
      case ECustomFieldDateFilterModes.None:
        return `${filter.fieldID}:d:none`
      case ECustomFieldDateFilterModes.Range:
        if (filter.startDate || filter.endDate) {
          return `${filter.fieldID}:d:${filter.startDate ||
            ''}_${filter.endDate || ''}`
        }
    }
    switch (filter.numberMode) {
      case ECustomFieldNumberFilterModes.Any:
        return `${filter.fieldID}:n:any`
      case ECustomFieldNumberFilterModes.None:
        return `${filter.fieldID}:n:none`
      case ECustomFieldNumberFilterModes.Range:
        if (
          this.isSetNumber(filter.minValue) ||
          this.isSetNumber(filter.maxValue)
        ) {
          const min = this.isSetNumber(filter.minValue)
            ? String(filter.minValue)
            : ''
          const max = this.isSetNumber(filter.maxValue)
            ? String(filter.maxValue)
            : ''
          return `${filter.fieldID}:n:${min}_${max}`
        }
    }
    return ''
  }

  private isSetNumber(value: number | null | undefined): boolean {
    return value !== null && value !== undefined && isFinite(value)
  }

  public parseFilters(encoded: string): ICustomFieldFilter[] {
    const result: ICustomFieldFilter[] = []
    if (!encoded || encoded.trim().length === 0) {
      return result
    }
    encoded.split('|').forEach(entry => {
      const parts = entry.split(':')
      if (parts.length < 3) {
        return
      }
      const fieldID = parseInt(parts[0], 10)
      if (isNaN(fieldID)) {
        return
      }
      const kind = parts[1]
      const value = parts.slice(2).join(':')
      switch (kind) {
        case 'o': {
          const optionIDs = value
            .split(OPTION_IDS_DELIMITER)
            .map(v => parseInt(v, 10))
            .filter(v => !isNaN(v))
          if (optionIDs.length > 0) {
            result.push({ fieldID, optionIDs })
          }
          break
        }
        case 'c':
          result.push({ fieldID, checked: value === '1' })
          break
        case 'd':
          if (value === ECustomFieldDateFilterModes.Any) {
            result.push({ fieldID, dateMode: ECustomFieldDateFilterModes.Any })
          } else if (value === ECustomFieldDateFilterModes.None) {
            result.push({ fieldID, dateMode: ECustomFieldDateFilterModes.None })
          } else {
            const [startDate, endDate] = value.split('_')
            if (startDate || endDate) {
              result.push({
                fieldID,
                dateMode: ECustomFieldDateFilterModes.Range,
                startDate: startDate || null,
                endDate: endDate || null,
              })
            }
          }
          break
        case 'n':
          if (value === ECustomFieldNumberFilterModes.Any) {
            result.push({
              fieldID,
              numberMode: ECustomFieldNumberFilterModes.Any,
            })
          } else if (value === ECustomFieldNumberFilterModes.None) {
            result.push({
              fieldID,
              numberMode: ECustomFieldNumberFilterModes.None,
            })
          } else {
            const [minRaw, maxRaw] = value.split('_')
            const minValue = minRaw ? Number(minRaw) : null
            const maxValue = maxRaw ? Number(maxRaw) : null
            if (this.isSetNumber(minValue) || this.isSetNumber(maxValue)) {
              result.push({
                fieldID,
                numberMode: ECustomFieldNumberFilterModes.Range,
                minValue: this.isSetNumber(minValue) ? minValue : null,
                maxValue: this.isSetNumber(maxValue) ? maxValue : null,
              })
            }
          }
          break
      }
    })
    return result
  }

  /* Short text for a filter chip, e.g. "Checked", "No date", "Mar 1 - Mar 15", or the chosen option names */
  public describeFilter(
    field: ICustomField,
    filter: ICustomFieldFilter | null,
  ): string {
    if (!filter) {
      return ''
    }
    if (filter.optionIDs && filter.optionIDs.length > 0) {
      return this.describeOptionIDs(field, filter.optionIDs)
    }
    if (filter.checked !== undefined && filter.checked !== null) {
      return filter.checked ? 'Checked' : 'Unchecked'
    }
    switch (filter.dateMode) {
      case ECustomFieldDateFilterModes.Any:
        return 'Has a date'
      case ECustomFieldDateFilterModes.None:
        return 'No date'
      case ECustomFieldDateFilterModes.Range: {
        const start = filter.startDate
          ? dayjs(filter.startDate).format('MMM D')
          : ''
        const end = filter.endDate ? dayjs(filter.endDate).format('MMM D') : ''
        if (start && end) {
          return start === end ? start : `${start} - ${end}`
        }
        return start ? `From ${start}` : `Until ${end}`
      }
    }
    switch (filter.numberMode) {
      case ECustomFieldNumberFilterModes.Any:
        return 'Has a value'
      case ECustomFieldNumberFilterModes.None:
        return 'No value'
      case ECustomFieldNumberFilterModes.Range: {
        const hasMin = this.isSetNumber(filter.minValue)
        const hasMax = this.isSetNumber(filter.maxValue)
        const min = hasMin ? this.formatNumber(field, filter.minValue) : ''
        const max = hasMax ? this.formatNumber(field, filter.maxValue) : ''
        if (hasMin && hasMax) {
          return min === max ? min : `${min} - ${max}`
        }
        return hasMin ? `At least ${min}` : `At most ${max}`
      }
    }
    return ''
  }

  /* Chosen option names for a dropdown filter chip, collapsing to a count past two */
  public describeOptionIDs(field: ICustomField, optionIDs: number[]): string {
    const names = (field.options || [])
      .filter(o => optionIDs.includes(o.id))
      .map(o => o.name)
    if (optionIDs.includes(NO_OPTION_FILTER_ID)) {
      names.push(NO_OPTION_FILTER_LABEL)
    }
    if (names.length === 0) {
      return ''
    }
    return names.length > 2 ? `${names.length} selected` : names.join(', ')
  }

  public createEmptyField(): ICustomField {
    return {
      id: 0,
      name: '',
      customFieldType: ECustomFieldTypes.Text,
      normalUserPermissions: ECustomFieldPermissions.ViewAndEdit,
      guestUserPermissions: ECustomFieldPermissions.None,
      numberType: ECustomFieldNumberTypes.Number,
      numberDecimalPlaces: 0,
      numberMinValue: null,
      numberMaxValue: null,
      numberLabel: '',
      numberLabelPosition: ECustomFieldNumberLabelPositions.Before,
      textType: ECustomFieldTextTypes.Unformatted,
      textMinLength: null,
      textMaxLength: null,
      sort: 0,
      showOnListings: false,
      showOnCreate: false,
      options: [],
    }
  }

  public createEmptyOption(sort: number): ICustomFieldOption {
    const colors = ColorAssistant.StatusColorOptions
    return {
      id: 0,
      name: '',
      color: colors[sort % colors.length],
      sort,
    }
  }
}()
